import type { PImage } from '../../engine/aliases'
import { distanceUtil } from '../../lib/distanceUtil-sidecar'
import { l } from '../../lib/imports'
import { mathUtils } from '../../lib/mathUtils-sidecar'
import { sprites } from '../../sprites/sprites-sidecar'
import { spritesUtil } from '../../sprites/spritesUtil-sidecar'
import { clickEater } from '../../uiParts/clickEater-sidecar'
import { voyageMessageCrewSelected } from './multi/voyageMessageCrewSelected-sidecar'
import { voyageCamera } from './voyageCamera-sidecar'
import { voyageColors } from './voyageColors-sidecar'
import { EVoyageCrewType, IVoyageCrew } from './voyageCrew'
import { voyageCrew } from './voyageCrew-sidecar'
import { voyageCrew_movable } from './voyageCrew_movable-sidecar'
import { voyageGameManager } from './voyageGameManager-sidecar'
import { voyageLayers } from './voyageLayers-sidecar'
import { voyageMovementIndicators } from './voyageMovementIndicators-sidecar'
import { voyagePicker_rect } from './voyagePicker_rect-sidecar'
import { voyageSounds } from './voyageSounds-sidecar'
import { voyageSystems_rack } from './voyageSystems_rack-sidecar'
import { EVoyageTile } from './voyageTileData'
import { voyageTilemap } from './voyageTilemap-sidecar'
import type { IVoyageTilemapPart } from './voyageTilemapPart'
import { voyageUiNav } from './voyageUiNav-sidecar'

export interface IVoyagePicker {
  imageCursor: PImage
  imageSelected: PImage
  imageWillSelect: PImage
  selectedCrew: IVoyageCrew | null
  hoveredCrew: IVoyageCrew | null
  hoveredSystem: IVoyageTilemapPart | null
  hoveredTile: IVoyageTilemapPart | null
}

let _obj: IVoyagePicker | null = null
export function get() {
  if (!_obj) {
    _obj = create()
  }
  return _obj
}

// export function selectFirstCrew() {
//   let cs = voyageCrew.getAll()
//   if (cs.length > 0) {
//     get().selectedCrew = cs[0]
//   }
// }

function create(): IVoyagePicker {
  let imageCursor = spritesUtil.createImage(
    voyageLayers.getLayers().shipAbove,
    sprites.voyTileCursor[0],
  )

  let imageWillSelect = spritesUtil.createImage(
    voyageLayers.getLayers().shipAbove,
    sprites.voyTileCursor[0],
  )

  let imageSelected = spritesUtil.createImage(
    voyageLayers.getLayers().shipAbove,
    sprites.voyTileCursor[0],
  )

  let obj: IVoyagePicker = {
    imageCursor,
    imageSelected,
    imageWillSelect,
    selectedCrew: null,
    hoveredCrew: null,
    hoveredSystem: null,
    hoveredTile: null,
  }

  return obj
}

export function clearSelection() {
  setSelection(null)
}

export function nextSelection(dir: -1 | 1) {
  let obj = get()
  let crs = voyageCrew.getAll()

  let idxStart = 0
  if (obj.selectedCrew) {
    idxStart = l.indexOf(crs, obj.selectedCrew)
  }

  idxStart += dir

  for (let i = 0; i < crs.length; i++) {
    let idx = mathUtils.betterMod(idxStart + i * dir, crs.length)
    let cr = crs[idx]
    if (cr.type === EVoyageCrewType.Crew && !cr.isDying && !cr.isDead) {
      //} || cr.type === EVoyageCrewType.Alien) {
      setSelection(cr, true)
      return
    }
  }
}

export function setSelection(cr: IVoyageCrew | null, center: boolean = false) {
  let obj = get()
  obj.selectedCrew = cr

  // Close ui?
  voyageUiNav.setOpen(false)

  if (cr) {
    obj.imageSelected.x = cr.x
    obj.imageSelected.y = cr.y
    obj.imageSelected.visible = true
    if (center) {
      voyageCamera.centerOn(cr.x, cr.y)
    }
  } else {
    obj.imageSelected.visible = false
  }

  voyageMessageCrewSelected.sendToOthers({
    crewId: cr ? cr.id : 0,
  })
}

export function update() {
  let obj = get()

  // Are we on a system?
  if (obj.selectedCrew) {
    if (obj.selectedCrew.tile) {
      if (obj.selectedCrew.tile.system) {
        let sys = obj.selectedCrew.tile.system
        if (sys.data.onSelectedCrewOn) {
          sys.data.onSelectedCrewOn(obj.selectedCrew)
        }
      }
    }
  }

  if (voyageGameManager.get().mode !== 'game' || clickEater.isCursorEaten()) {
    obj.imageCursor.visible = false
    obj.imageSelected.visible = false
    obj.imageWillSelect.visible = false
    return
  }
  obj.imageCursor.visible = true
  obj.imageSelected.visible = true
  obj.imageWillSelect.visible = true

  let mij = voyageTilemap.getIJAtMouse()
  let mxy = voyageTilemap.convertIJtoXY(mij.i, mij.j)
  let mou = voyageCamera.getAdjustedMousePosition()

  let tHere = voyageTilemap.getSafe(mij.i, mij.j)
  obj.hoveredTile = tHere

  obj.imageCursor.x = mxy.x //mou.max
  obj.imageCursor.y = mxy.y //mou.may

  if (obj.selectedCrew && obj.selectedCrew.isDead) {
    obj.selectedCrew = null
  }

  if (obj.selectedCrew) {
    obj.imageSelected.x = obj.selectedCrew.x
    obj.imageSelected.y = obj.selectedCrew.y
  }

  let closestCrew: IVoyageCrew | null = null as IVoyageCrew | null
  let closestCrewDistance = 16

  let closestHoverCrew: IVoyageCrew | null = null as IVoyageCrew | null

  l.forEach(voyageCrew.getAll(), (c) => {
    let dist = distanceUtil.distanceNormalXY(mou, c)
    if (dist < closestCrewDistance) {
      closestHoverCrew = c

      if (c.type === EVoyageCrewType.Crew && !c.isDead) {
        closestCrewDistance = dist
        closestCrew = c
      }
    }
  })
  obj.hoveredCrew = closestCrew || closestHoverCrew

  if (voyagePicker_rect.isRectSelecting()) {
    closestHoverCrew = null
    closestCrew = voyagePicker_rect.getSelectedInRect()
    obj.imageCursor.visible = false
  }

  if (closestCrew) {
    obj.imageWillSelect.visible = true
    obj.imageWillSelect.x = closestCrew.x
    obj.imageWillSelect.y = closestCrew.y
  } else {
    obj.imageWillSelect.visible = false
  }

  if (obj.selectedCrew) {
    obj.imageSelected.visible = true
    obj.imageSelected.x = obj.selectedCrew.x
    obj.imageSelected.y = obj.selectedCrew.y
    obj.imageSelected.tint = voyageColors.yellow
  } else {
    obj.imageSelected.visible = false
  }

  obj.hoveredSystem = null
  if (tHere) {
    if (tHere.tile3.type === EVoyageTile.System) {
      obj.hoveredSystem = tHere
    }
    if (tHere.tile3.type === EVoyageTile.Elevator) {
      obj.hoveredSystem = tHere // 'Elevator ' + tHere.text3
    }
    if (tHere.tile3.type === EVoyageTile.MedPad) {
      obj.hoveredSystem = tHere //'Medical Pad ' + tHere.text3
    }
    // if (tHere.tile3.type === EVoyageTile.) {
    //   obj.hoveredSystem = 'Elevator' + tHere.text3
    // }
  }

  let isRectSelecting = voyagePicker_rect.isRectSelecting()

  voyagePicker_rect.update()

  if (isRectSelecting) {
    // handled in voyagePicker
  } else if (clickEater.isLeftFirstUpAndUneaten()) {
    let t = voyageTilemap.getSafe(mij.i, mij.j)
    if (t) {
      if (closestCrew) {
        setSelection(closestCrew)
        voyageSounds.playSelected()
        clickEater.tempEat()
      } else {
        // if (obj.selectedCrew) {
        //   // Move
        //   voyageCrew_movable.pathTo(obj.selectedCrew, mij.i, mij.j)
        // }
      }
    }
  }

  if (!isRectSelecting) {
    if (clickEater.isRightFirstDownAndUneaten()) {
      // clearSelection()
      // Move
      let t = voyageTilemap.getSafe(mij.i, mij.j)
      if (t) {
        if (!closestCrew || closestCrew === obj.selectedCrew) {
          if (obj.selectedCrew) {
            if (distanceUtil.isAtIJ(obj.selectedCrew, mij)) {
              console.log('click on selected!')
              // Drop / pickup

              voyageSystems_rack.swapItemHere(obj.selectedCrew, true)

              // let droppable = voyageCrew.getFirstDroppableItem(obj.selectedCrew)
              // let pickupable = t.items[0] || null
              // if (droppable || pickupable) {
              //   if (voyageWebsockets.getIsHost()) {
              //     if (droppable && pickupable) {
              //       voyageMessageCrewCarry.sendMessageAndImmediatelyExecute({
              //         command: EVoyageCrewCarryCommand.Swap,
              //         type: droppable.type,
              //         carryId1: droppable.id,
              //         carryId2: pickupable.id,
              //         crewId: obj.selectedCrew.id,
              //         i: mij.i,
              //         j: mij.j,
              //       })
              //     } else if (droppable) {
              //       voyageMessageCrewCarry.sendMessageAndImmediatelyExecute({
              //         command: EVoyageCrewCarryCommand.Drop,
              //         type: droppable.type,
              //         carryId1: droppable.id,
              //         carryId2: 0,
              //         crewId: obj.selectedCrew.id,
              //         i: mij.i,
              //         j: mij.j,
              //       })
              //     } else if (pickupable) {
              //       voyageMessageCrewCarry.sendMessageAndImmediatelyExecute({
              //         command: EVoyageCrewCarryCommand.Pickup,
              //         type: pickupable.type,
              //         carryId1: pickupable.id,
              //         carryId2: 0,
              //         crewId: obj.selectedCrew.id,
              //         i: mij.i,
              //         j: mij.j,
              //       })
              //     }
              //   }
              // }
            } else {
              // Move
              if (voyageCrew_movable.pathTo(obj.selectedCrew, mij.i, mij.j)) {
                let mi = voyageMovementIndicators.create(voyageColors.yellow)
                voyageMovementIndicators.moveToIJ(mi, mij)
              }
            }
          }
        }
      }
    }
  }
}
